'use client';

import { Box, BoxProps, Chip, styled, Typography } from '@mui/material';
import { Dispatch, FunctionComponent, SetStateAction } from 'react';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { DynamicRequestBody } from '../../../schemas/DynamicRequestBody';
import { ReactComponent as AllProductSvg } from '../../../static/product_image/allproduct.svg';
import { useNavigate } from 'react-router-dom';
import { FilterParameterAndParameterValues } from '../ProductHome';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { DetectedDefinition } from '../../../schemas/DetectedDefinition';
import {
    getDisplayNameForFilterParameter,
    getDisplayNameForReturnParameterValue,
} from '../../../utils/StringRelated';
import { getDefinitionDisplayName } from '../../../utils/DefinitionDisplay';
import { useLocale } from '../../../contexts/LocaleProvider';
import { LanguageMapping } from '../../../utils/LanguageMapping';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';

/** ─────────────────────────────
 *  視覺統一常數
 *  ───────────────────────────── */
const CHIP_H = 26; // 所有 chip 與 SVG 區塊高度
const chipText = {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: `${CHIP_H}px`, // 與高度一致，確保文字垂直置中
} as const;

/** 「All Products」SVG + 置中文字（統一 line-height） */
// const SvgWithText = ({ text = 'All Products' }: { text?: string }) => {
//     return (
//         <Box
//             sx={{
//                 display: 'inline-flex',
//                 alignItems: 'center',
//                 position: 'relative',
//                 height: CHIP_H,
//             }}
//             data-testid={'all-product-chip'}
//         >
//             <AllProductSvg
//                 style={{
//                     display: 'block',
//                     height: '100%',
//                     width: 'auto',
//                 }}
//             />
//             <Typography
//                 component="span"
//                 sx={{
//                     position: 'absolute',
//                     top: '50%',
//                     left: '50%',
//                     transform: 'translate(-50%, -50%)',
//                     pointerEvents: 'none',
//                     color: '#000',
//                     whiteSpace: 'nowrap',
//                     ...chipText,
//                 }}
//             >
//                 {text}
//             </Typography>
//         </Box>
//     );
// };

/** 「All Products」SVG + 置中文字（可自動拉寬、可自訂 data-testid） */
const SvgWithText: FunctionComponent<
    Omit<BoxProps, 'children'> & {
        text?: string;
        /** 最小寬度，避免太短；可視覺需要調整或由 sx 覆蓋 */
        minWidth?: number | string;
    }
> = ({ text = 'All Products', minWidth = 80, sx, ...boxProps }) => {
    return (
        <Box
            {...boxProps} // 這裡會把 data-testid 等 DOM 屬性直接轉到外層 <div>
            sx={[
                {
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: CHIP_H,
                    minWidth,
                    px: 1.5, // 左右內距，控制文字與邊框距離；需要更多空間可加大
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {/* SVG 當背景鋪滿容器，寬度跟著容器變化 → 文字多就自動變長 */}
            <AllProductSvg
                preserveAspectRatio="none"
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    pointerEvents: 'none',
                }}
            />
            <Typography
                component="span"
                sx={{
                    position: 'relative', // 疊在 SVG 上方
                    zIndex: 1,
                    whiteSpace: 'nowrap', // 不換行；超長就讓容器拉長
                    ...chipText,
                }}
            >
                {text}
            </Typography>
        </Box>
    );
};

/** 共用的 Chip 樣式（統一字體、行高、內距與 box-model） */
const baseChipStyles = {
    height: `${CHIP_H}px`,
    boxSizing: 'border-box' as const,
    '& .MuiChip-label': {
        padding: '0 10px',
        ...chipText,
    },
};

const MainLevelChip = styled(Chip)(() => ({
    ...baseChipStyles,
    backgroundColor: '#F2F2F2',
    border: '1px solid #E5673E', // 與其他 chip 統一 1px
    borderRadius: '4px 0 0 4px',
    position: 'relative',
    zIndex: 1,
    '& .MuiChip-deleteIcon': {
        display: 'none',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        right: '-10px',
        transform: 'translateY(-50%)',
        borderTop: `${CHIP_H / 2}px solid transparent`,
        borderBottom: `${CHIP_H / 2}px solid transparent`,
        borderLeft: '10px solid #F2F2F2',
        zIndex: 1,
    },
}));

export const ConditionChip = styled(Chip)(() => ({
    ...baseChipStyles,
    backgroundColor: '#B75332',
    border: '1px solid #191A1B',
    color: '#fff',
    borderRadius: '13px',
}));

const SubLevelChip = styled(Chip)(() => ({
    ...baseChipStyles,
    backgroundColor: '#B75332',
    border: '1px solid #191A1B',
    color: '#fff',
    borderRadius: '0 13px 13px 0',
    '& .MuiChip-deleteIcon': {
        color: '#fff',
        margin: '0 4px 0 -4px',
    },
}));

const CustomDeleteIcon = () => (
    <ClearOutlinedIcon style={{ color: 'white', borderRadius: '13px' }} />
);

interface AppliedFilterInfoProps {
    multipleSelectKeys: Map<string, FilterParameterAndParameterValues>;
    isDataSheet: boolean;
    singleSelectKey: Map<string, FilterParameterAndParameterValues>;
    setGlobalFilterState: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    productField: DetectedDefinition;
    onlyHandleFilterChange?: (parameter?: string) => void;
    setGlobalFilterStateAfterApply: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    productChildField: DetectedDefinition | undefined;
    contentTree: ProductsContent;
}

const AppliedFilterInfo: FunctionComponent<AppliedFilterInfoProps> = ({
    singleSelectKey,
    multipleSelectKeys,
    isDataSheet,
    setGlobalFilterState,
    onlyHandleFilterChange,
    setGlobalFilterStateAfterApply,
    productField,
    productChildField,
    contentTree,
}) => {
    // for locales
    const { locale } = useLocale();

    const handleClear = (parameter: string) => {
        setGlobalFilterState((prevGlobal: DynamicRequestBody | undefined) => {
            if (prevGlobal === undefined) {
                return prevGlobal;
            }

            const updatedGlobal = { ...prevGlobal };
            if (parameter in updatedGlobal) {
                delete updatedGlobal[parameter];
            }
            return updatedGlobal;
        });
        setGlobalFilterStateAfterApply((prevGlobal: DynamicRequestBody | undefined) => {
            if (!prevGlobal) {
                return prevGlobal;
            }

            const updatedGlobal = { ...prevGlobal };
            if (parameter in updatedGlobal) {
                delete updatedGlobal[parameter];
            }
            return updatedGlobal;
        });

        if (onlyHandleFilterChange) {
            onlyHandleFilterChange(parameter);
        }
    };

    const hasChildField = Boolean(productChildField);

    const navigate = useNavigate();
    const handleNavigateToAllProductPage = () => {
        navigate(`/browse/products`);
    };

    const { isMaxScreen, isScreenXxl } = useResponsive();

    // 當不是 isMaxScreen 或 isScreenXxl 時，包上 PageContainer
    const shouldWrapWithPageContainer = !(isMaxScreen || isScreenXxl);

    const getFilterChipsPadding = () => {
        if (shouldWrapWithPageContainer) {
            return hasChildField ? '5px 0px 0px 0px' : '10px 0px 0px 0px';
        }
        return hasChildField ? '5px 20px 0px 20px' : '10px 20px 0px 20px';
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                padding: getFilterChipsPadding(),
                width: '100%',
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: 16,
            }}
            data-testid={'filter-chips-zone'}
        >
            {!isDataSheet && (
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        fontSize: 16,
                    }}
                >
                    {contentTree.filters.labels.appliedFilter.content}
                </Typography>
            )}
            {hasChildField ? (
                <Box
                    sx={{
                        display: 'flex',
                        // 可視需要加上 position: 'relative'，用來控制 z-index 等，但現在只是重疊就不用
                        paddingTop: '5px',
                    }}
                >
                    {/* 1. 左邊的 SVG */}
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <SvgWithText
                            text={`${contentTree.filters.labels.allProducts.content}`}
                            data-testid="all-product-chip"
                        />
                    </Box>

                    {/* 2. 讓 MainLevelChip 用負的 marginLeft 來和 SVG 有些重疊 */}
                    <Box sx={{ marginLeft: '-10px', zIndex: 1 }}>
                        <SvgWithText
                            text={`${getDefinitionDisplayName(
                                productField,
                                LanguageMapping.get(locale) || 'ENGLISH'
                            )}`}
                            data-testid="tree-chip"
                        />
                    </Box>

                    {/* 3. 接著顯示 SubLevelChip，正常距離即可 */}
                    <Box sx={{ marginLeft: '-15px' }}>
                        <SubLevelChip
                            label={getDefinitionDisplayName(
                                productChildField,
                                LanguageMapping.get(locale) || 'ENGLISH'
                            )}
                            onDelete={() => {}}
                            onClick={() => handleClear('79')}
                            deleteIcon={<CustomDeleteIcon />}
                            data-testid={'lv1-chip'}
                            sx={{
                                // 這裡針對 Chip 本身 (或它內部的 label) 加上想要的 padding-left
                                '& .MuiChip-label': {
                                    paddingLeft: '15px',
                                    paddingRight: '8px',
                                    marginTop: '1px',
                                },
                            }}
                        />
                    </Box>
                </Box>
            ) : (
                /**
                 * 沒有 childField 時：
                 * 只顯示 Allprouct + productField
                 */
                <Box sx={{ display: 'flex' }}>
                    <MainLevelChip
                        label={`${contentTree.filters.labels.allProducts.content}`}
                        onDelete={() => {}}
                        data-testid={'all-product-chip'}
                    />
                    <SubLevelChip
                        label={getDefinitionDisplayName(
                            productField,
                            LanguageMapping.get(locale) || 'ENGLISH'
                        )}
                        onDelete={() => {}}
                        onClick={() => handleNavigateToAllProductPage()}
                        deleteIcon={<CustomDeleteIcon />}
                        data-testid={'tree-chip'}
                        sx={{
                            '& .MuiChip-label': {
                                paddingLeft: '15px',
                                paddingRight: '10px', // 原先默認有 10px，視需求保留
                            },
                        }}
                    />
                </Box>
            )}
            {/* 多選參數：直接顯示 key 與固定訊息 */}
            {Array.from(multipleSelectKeys).map(([key, ppv]) => (
                <ConditionChip
                    data-testid={`${ppv.parameterName}-values`}
                    key={key}
                    label={`${getDisplayNameForFilterParameter(ppv, LanguageMapping.get(locale) || 'ENGLISH')} = Multiple Values`}
                    onDelete={() => {}}
                    onClick={() => handleClear(key)}
                    deleteIcon={<CustomDeleteIcon />}
                    sx={{ marginBottom: '1px' }}
                />
            ))}

            {Array.from(singleSelectKey).map(([key, ppv]) => {
                if (key === '79') {
                    return null;
                }

                let label =
                    ppv.parameterValues[0]?.value === true
                        ? `${getDisplayNameForFilterParameter(ppv, LanguageMapping.get(locale) || 'ENGLISH')}`
                        : `${getDisplayNameForFilterParameter(ppv, LanguageMapping.get(locale) || 'ENGLISH')} = ${getDisplayNameForReturnParameterValue(ppv.parameterValues[0], LanguageMapping.get(locale) || 'ENGLISH')}`;

                return (
                    <ConditionChip
                        data-testid={`${ppv.parameterName}-value`}
                        key={key}
                        label={label}
                        onDelete={() => {}}
                        onClick={() => handleClear(key)}
                        deleteIcon={<CustomDeleteIcon />}
                        sx={{ marginBottom: '1px' }}
                    />
                );
            })}
        </Box>
    );
};

export default AppliedFilterInfo;
